import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export const sessionCacheFeatureKey = 'sessionCache'

export enum SessionCacheName {
  CwTransactions = 'CwTransactions',
  ExtTransactions = 'ExtTransactions',
  Positions = 'Positions',
  /**
   * Flex (2.0) caches, one slot per grid source the 2.0 workbenches can be pointed at. `Flex*` is
   * the Enfusion-side (api) feed, `FlexExternal*` the Prometheus-side (prom) feed. There is no Flex
   * cache for Clearwater (cw) — Base and Comparison are each pinned to one fixed source today (see
   * `disabledRoles` in each panel's `source-registry.ts`), so nothing has needed a cw slot. Each
   * panel host (e.g. `positions-2-panel.component.ts`) reads the fixed selector for its role
   * straight into `<hs-cache-load-listener>`, the same gate Positions/Transactions 1.0 use.
   */
  FlexPositions = 'FlexPositions',
  FlexExternalPositions = 'FlexExternalPositions',
  FlexTransactions = 'FlexTransactions',
  FlexExternalTransactions = 'FlexExternalTransactions',
}

/**
 * string values must match what is returned by the SSE
 * moved this enum from server-sent-events.model.ts to here to avoid a mapping layer which could get out of sync
 */
export enum SessionCacheStatus {
  'STARTED' = 'Started',
  'PARTIAL' = 'Partial',
  'DONE' = 'Done',
  'ERROR' = 'Error',
  'POST_LOAD_DONE' = 'Post Load Done',
  'UNKNOWN' = 'Unknown',
}

export type SessionCacheRedux = {
  status: Record<SessionCacheName, SessionCacheStatus>
  //leaving room for more keys in future
}

export const sessionCacheInitialState: SessionCacheRedux = {
  status: {
    CwTransactions: SessionCacheStatus.UNKNOWN,
    ExtTransactions: SessionCacheStatus.UNKNOWN,
    Positions: SessionCacheStatus.UNKNOWN,
    FlexPositions: SessionCacheStatus.UNKNOWN,
    FlexExternalPositions: SessionCacheStatus.UNKNOWN,
    FlexTransactions: SessionCacheStatus.UNKNOWN,
    FlexExternalTransactions: SessionCacheStatus.UNKNOWN,
  },
}

/**
 * usage
 * ```
 * store.dispatch(
 *    setSessionCacheStatus({
 *      name: SessionCacheName.CWTRANS,
 *      status: SessionCacheStatus.STARTED,
 *    }),
 *  )
 * ```
 */
export const sessionCacheSlice = createSlice({
  name: sessionCacheFeatureKey,
  initialState: sessionCacheInitialState,
  reducers: {
    resetSessionCacheStatus: oldState => {
      return sessionCacheInitialState
    },
    setSessionCacheStatus: (
      oldState,
      action: PayloadAction<{
        name: SessionCacheName
        status: SessionCacheStatus
      }>,
    ) => {
      const newStatus = { ...oldState.status }
      newStatus[action.payload.name] = action.payload.status
      return {
        ...oldState,
        status: newStatus,
      }
    },
  },
})

/**
 * These actions should only be used within the SessionsService and tests.
 */
export const { setSessionCacheStatus, resetSessionCacheStatus } =
  sessionCacheSlice.actions

export default sessionCacheSlice.reducer
